import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Box } from "@mui/material";
import AppLayout from "../components/AppLayout";
import StyledButton from "../components/StyledButton";
import Loading from "../components/Loading";
import { COLORS } from "constants/colors";
import { useUrlParams } from "hooks/useUrlParams";
import { soulBackend } from "backend";

// Payment status constants
const PAYMENT_STATUS = {
  COMPLETED: "COMPLETED",
  CREATED: "CREATED",
  ERROR: "ERROR",
};

// Helper function to get status content
const getStatusContent = (status, navigate, successPath, failPath) => {
  const navigateToSuccess = () => navigate(successPath);
  const navigateToFail = () => navigate(failPath);

  switch (status) {
    case PAYMENT_STATUS.CREATED:
      return {
        message: "購入が完了しておりません。\n再度購入してください。",
        buttonLabel: "購入画面へ",
        onClick: navigateToFail,
      };
    case PAYMENT_STATUS.COMPLETED:
      return {
        message: "決済が完了しました。",
        buttonLabel: "完了",
        onClick: navigateToSuccess,
      };
    case PAYMENT_STATUS.ERROR:
    default:
      return {
        message: "エラーが発生しました。",
        buttonLabel: "購入画面へ",
        onClick: navigateToFail,
      };
  }
};

// Custom hook for payment status management
const usePaymentStatus = (
  merchantPaymentId,
  soulId,
  purchaseType,
  ticketKey,
  work
) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkPaymentStatus = async () => {
    if (!merchantPaymentId) {
      setStatus(PAYMENT_STATUS.ERROR);
      setLoading(false);
      return;
    }

    try {
      const { status: paymentStatus } = await soulBackend(
        work
      ).getPaypayStatus(merchantPaymentId, {
        soulId,
        purchaseType,
        ticketKey,
      });

      if (paymentStatus === PAYMENT_STATUS.COMPLETED) {
        setStatus(PAYMENT_STATUS.COMPLETED);
      } else if (paymentStatus === PAYMENT_STATUS.CREATED) {
        setStatus(PAYMENT_STATUS.CREATED);
      } else {
        setStatus(PAYMENT_STATUS.ERROR);
      }
    } catch (error) {
      console.error("Error fetching payment status:", error);
      setStatus(PAYMENT_STATUS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkPaymentStatus();
  }, [merchantPaymentId, soulId, purchaseType, ticketKey]);

  return { status, loading };
};

// Component for status content
const StatusContent = ({ content }) => (
  <Box
    sx={{
      width: "88%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: COLORS.BACKGROUND_WHITE,
      borderRadius: "5px",
      padding: 4,
      gap: 4,
    }}
  >
    <Typography
      variant="h6"
      sx={(theme) => ({
        mt: 1,
        whiteSpace: "pre-line",
        textAlign: "center",
        color: COLORS.TEXT_BLACK,
      })}
    >
      {content.message}
    </Typography>

    <StyledButton
      variant="contained"
      sx={{
        width: "80%",
        color: COLORS.TEXT_BLACK,
        fontWeight: "bold",
        border: `2px solid ${COLORS.TEXT_BLACK}`,
      }}
      onClick={content.onClick}
    >
      {content.buttonLabel}
    </StyledButton>
  </Box>
);

function PayPayCheck({ LayoutComponent = AppLayout }) {
  const { soulId, lng, work } = useUrlParams();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const merchantPaymentId = queryParams.get("merchant_payment_id");
  const successPage = queryParams.get("success_page");
  const failPage = queryParams.get("fail_page");
  const purchaseType = queryParams.get("purchase_type");
  const ticketKey = queryParams.get("ticket_key");

  const successPath = successPage
    ? `/${work}/${successPage}?soul_id=${soulId}&lng=${lng}`
    : `/${work}/how-to-use?soul_id=${soulId}&lng=${lng}`;
  const failPath = failPage
    ? `/${work}/${failPage}?soul_id=${soulId}&lng=${lng}`
    : purchaseType === "tamil"
    ? `/${work}/exchange_money?soul_id=${soulId}&lng=${lng}`
    : `/${work}/tickets?soul_id=${soulId}&lng=${lng}`;

  const { status, loading } = usePaymentStatus(
    merchantPaymentId,
    soulId,
    purchaseType,
    ticketKey,
    work
  );

  if (loading) {
    return (
      <LayoutComponent page="PayPayCheck">
        <Loading />
      </LayoutComponent>
    );
  }

  const content = getStatusContent(status, navigate, successPath, failPath);

  return (
    <LayoutComponent page="PayPayCheck">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "100vh",
        }}
      >
        <StatusContent content={content} />
      </Box>
    </LayoutComponent>
  );
}

export default PayPayCheck;
